import Input from '@jetbrains/ring-ui/components/input/input'
import Radio from '@jetbrains/ring-ui/components/radio/radio'
import {format, formatISO, parseISO, isValid, isAfter, addYears, isFuture} from 'date-fns'
import {useCallback, memo, useReducer, useEffect} from 'react'

import {BS} from '../../../types/BS_types'

import styles from './ScheduleBuild.css'

enum ScheduleType {
  ASAP = 'ASAP',
  DATE_TIME = 'DATE_TIME',
}

type StateType = {
  date: Date | null
  error: string | null
  scheduleType: ScheduleType
  minDate: Date
  maxDate: Date
}

type Action =
  | {
      type: 'SET_DATE'
      payload: string
    }
  | {
      type: 'SET_SCHEDULE_TYPE'
      payload: ScheduleType
    }

const DEFAULT_ADD_YEARS_TO_SCHEDULE = 5
const reducer = (state: StateType, action: Action) => {
  switch (action.type) {
    case 'SET_SCHEDULE_TYPE': {
      return {
        ...state,
        scheduleType: action.payload,
        error: null,
        date:
          (state.date == null && action.payload === ScheduleType.DATE_TIME) ||
          (state.date != null && !isFuture(state.date))
            ? null
            : state.date,
      }
    }

    case 'SET_DATE':
      const date = parseISO(action.payload)
      if (isValid(date)) {
        const error = isAfter(date, state.minDate)
          ? null
          : 'Please select a date and time that is in the future'
        return {
          ...state,
          date,
          error,
        }
      } else {
        return {
          ...state,
          date: null,
          error: 'Invalid date',
        }
      }
    default:
      return state
  }
}

const initialState: StateType = {
  date: null,
  scheduleType: ScheduleType.ASAP,
  error: null,
  minDate: new Date(),
  maxDate: addYears(new Date(), DEFAULT_ADD_YEARS_TO_SCHEDULE),
}

function ScheduleBuild() {
  const [{date, error, minDate, maxDate, scheduleType}, dispatch] = useReducer(
    reducer,
    initialState,
    state => ({
      ...state,
      minDate: new Date(),
    }),
  )

  const changeDate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({type: 'SET_DATE', payload: e.target.value})
  }, [])

  const onScheduleTypeChange = useCallback((type: string) => {
    dispatch({type: 'SET_SCHEDULE_TYPE', payload: type as ScheduleType})
  }, [])

  useEffect(() => {
    if (scheduleType === ScheduleType.DATE_TIME && (error != null || date == null)) {
      BS?.RunBuildDialog?.disableSubmit()
    } else {
      BS?.RunBuildDialog?.enableSubmit()
    }
  }, [date, error, scheduleType])

  return (
    <div>
      <Radio value={scheduleType} onChange={onScheduleTypeChange}>
        <Radio.Item className={styles.radioButton} value={ScheduleType.ASAP}>
          {'As soon as possible'}
        </Radio.Item>
        <Radio.Item className={styles.radioButton} value={ScheduleType.DATE_TIME}>
          {'At specific date & time'}
        </Radio.Item>
      </Radio>

      {scheduleType === ScheduleType.DATE_TIME ? (
        <div className={styles.container}>
          <Input
            className={styles.input}
            inputClassName={styles.inputInner}
            onChange={changeDate}
            name={'fake-scheduled-time-field'}
            type={'datetime-local'}
            min={format(minDate, `yyyy-MM-dd'T'HH:mm`)}
            max={format(maxDate, `yyyy-MM-dd'T'HH:mm`)}
            value={date ? format(date, `yyyy-MM-dd'T'HH:mm`) : ''}
            error={error}
          />
          <input
            required
            name={'scheduledTime'}
            type={'hidden'}
            value={date ? formatISO(date) : ''}
          />
        </div>
      ) : null}
    </div>
  )
}

export default memo(ScheduleBuild)
